import React, { useState } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Avatar, 
  Box, 
  Container, 
  Card, 
  CardContent, 
  Grid, 
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  AccountCircle, 
  Logout, 
  Settings, 
  BarChart, 
  People, 
  Security,
  Menu as MenuIcon,
  QrCodeScanner,
  Inventory,
  History,
  ArrowForwardIos
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import BarcodeScanner from './BarcodeScanner';
import ProductPhotoCapture from './ProductPhotoCapture';
import ProductDetails from './ProductDetails';
import { productsAPI } from '../services/api';
import { ScanResult, Product } from '../types';

const Dashboard: React.FC = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanResult, setScanResult] = useState<ScanResult | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [photoCaptureOpen, setPhotoCaptureOpen] = useState(false);
  const [currentBarcode, setCurrentBarcode] = useState<string>('');
  const [productDetailsOpen, setProductDetailsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [scanHistory, setScanHistory] = useState<any[]>([]);

  // Load scan history on component mount
  React.useEffect(() => {
    loadScanHistory();
  }, []);

  const loadScanHistory = async () => {
    try {
      console.log('Loading scan history...');
      const history = await productsAPI.getMyScanHistory();
      console.log('Scan history loaded:', history);
      // Sort by most recent first
      const sortedHistory = history.sort((a: any, b: any) => 
        new Date(b.scanned_at).getTime() - new Date(a.scanned_at).getTime()
      );
      console.log('Sorted scan history:', sortedHistory);
      setScanHistory(sortedHistory);
    } catch (error) {
      console.error('Error loading scan history:', error);
    }
  };

  const handleHistoryItemClick = async (barcode: string) => {
    try {
      const product = await productsAPI.getByBarcode(barcode);
      setSelectedProduct(product);
      setProductDetailsOpen(true);
    } catch (error) {
      console.error('Error loading product:', error);
      setScanResult({
        found: false,
        product: undefined,
        message: 'Product not found in database'
      });
      setSnackbarOpen(true);
    }
  };

  if (!user) return null;

  // Debug: Log user data to see if picture is available
  console.log('User data:', user);
  console.log('User picture:', user.picture);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleScanResult = async (barcode: string) => {
    try {
      console.log('Scanning barcode:', barcode);
      const result = await productsAPI.scanProduct(barcode);
      console.log('Scan result:', result);
      
      if (result.found && result.product) {
        // Product found - show product details
        console.log('Product found, showing details and reloading history');
        setSelectedProduct(result.product);
        setProductDetailsOpen(true);
        // Reload scan history to include the new scan
        await loadScanHistory();
        setScannerOpen(false);
      } else {
        // Product not found - check if user can make requests
        if (user && user.remaining_requests !== undefined && user.remaining_requests <= 0) {
          setScanResult({
            found: false,
            product: undefined,
            message: "Ai atins limita de cereri pentru această lună. Contactează administratorul pentru a crește limita."
          });
          setSnackbarOpen(true);
          setScannerOpen(false);
          return;
        }
        
        // Product not found - start photo capture process
        console.log('Product not found, starting photo capture');
        setCurrentBarcode(barcode);
        setPhotoCaptureOpen(true);
        setScannerOpen(false);
      }
    } catch (error) {
      console.error('Error scanning product:', error);
      setScanResult({
        found: false,
        product: undefined,
        message: `Error scanning barcode: ${barcode}`
      });
      setSnackbarOpen(true);
      setScannerOpen(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleScannerClose = () => {
    setScannerOpen(false);
  };

  const handlePhotoCaptureComplete = async (ingredientsPhoto: string, nutritionalPhoto: string) => {
    try {
      console.log('Analyzing photos for barcode:', currentBarcode);
      const response = await productsAPI.analyzePhotos(currentBarcode, ingredientsPhoto, nutritionalPhoto);
      console.log('Photo analysis response:', response);
      
      if (response.success && response.product) {
        // Show the product details immediately
        console.log('Photo analysis successful, showing product and reloading history');
        setSelectedProduct(response.product);
        setProductDetailsOpen(true);
        // Reload scan history to include the new scan
        await loadScanHistory();
      } else {
        console.log('Photo analysis failed:', response.error);
        setScanResult({
          found: false,
          product: undefined,
          message: response.error || 'Failed to analyze product photos'
        });
        setSnackbarOpen(true);
      }
      
      setPhotoCaptureOpen(false);
    } catch (error) {
      console.error('Error analyzing photos:', error);
      setScanResult({
        found: false,
        product: undefined,
        message: 'Failed to analyze product photos'
      });
      setSnackbarOpen(true);
      setPhotoCaptureOpen(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <AppBar position="static" elevation={1}>
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <Security sx={{ mr: 2, fontSize: { xs: 20, sm: 24 } }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
            Analizez.eu
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Button
              variant="text"
              onClick={handleMenuOpen}
              color="inherit"
              startIcon={
                <Avatar 
                  src={user.picture} 
                  alt={user.name}
                  sx={{ width: 24, height: 24 }}
                />
              }
              sx={{ 
                textTransform: 'none',
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                py: { xs: 0.5, sm: 1 },
                px: { xs: 1, sm: 2 },
                display: 'flex' // Show on all screen sizes
              }}
            >
              {user.name}
            </Button>
            
            {/* <IconButton
              size="large"
              onClick={handleMenuOpen}
              color="inherit"
              sx={{ p: { xs: 0.5, sm: 1 } }}
            >
              <AccountCircle sx={{ fontSize: { xs: 20, sm: 24 } }} />
            </IconButton> */}
            
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              PaperProps={{
                sx: {
                  minWidth: 200,
                  mt: 1
                }
              }}
            >
              {/* User Info Header */}
              <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar 
                    src={user.picture} 
                    alt={user.name}
                    sx={{ width: 40, height: 40 }}
                  />
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {user.name}
                    </Typography>
                      {/* <Typog.raphy variant="caption" color="text.secondary">
                        {user.email}
                      </Typography> */}
                    {/* <Chip 
                      label={user.role} 
                      color={user.role === 'admin' ? 'secondary' : 'primary'}
                      size="small"
                      sx={{ 
                        fontSize: '0.7rem',
                        height: 20,
                        mt: 0.5
                      }}
                    /> */}
                  </Box>
                </Box>
              </Box>

              {/* Admin Actions */}
              {isAdmin && (
                <>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/users'); }}>
                    <People sx={{ mr: 1 }} />
                    Gestionează Utilizatorii
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/products'); }}>
                    <Inventory sx={{ mr: 1 }} />
                    Gestionează Produsele
                  </MenuItem>
                  <MenuItem onClick={() => { handleMenuClose(); navigate('/admin/analytics'); }}>
                    <BarChart sx={{ mr: 1 }} />
                    Vezi Analitica
                  </MenuItem>
                </>
              )}

              {/* User Actions */}
              {/* imit */}

              {/* Divider */}
              <Box sx={{ borderTop: 1, borderColor: 'divider', my: 0.5 }} />

              {/* Logout */}
              <MenuItem onClick={handleLogout}>
                <Logout sx={{ mr: 1 }} />
                Deconectare
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: { xs: 2, sm: 4 }, px: { xs: 1, sm: 2 } }}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {/* Welcome Card */}
          <Grid size={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" gutterBottom sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
                  Bine ai venit înapoi, {user.name}!
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                  Scanează codurile de bare pentru a analiza produsele și a vizualiza istoricul scanărilor.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Scan Button */}
          <Grid size={12}>
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <QrCodeScanner sx={{ fontSize: 80, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Scanează Codul de Bare al Produsului
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  Îndreaptă camera spre codul de bare al produsului pentru a analiza informațiile de sănătate
                </Typography>
                
                {/* Request limit display */}
                {user && user.role !== 'admin' && (
                  <Box sx={{ mb: 3 }}>
                    {user.remaining_requests !== undefined ? (
                      user.remaining_requests > 0 ? (
                        <Chip 
                          label={`${user.remaining_requests} cereri rămase această lună`}
                          color="success"
                          variant="outlined"
                          sx={{ mb: 2 }}
                        />
                      ) : (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Ai atins limita de cereri pentru această lună. Contactează administratorul pentru a crește limita.
                        </Alert>
                      )
                    ) : (
                      <Chip 
                        label="Cereri nelimitate"
                        color="info"
                        variant="outlined"
                        sx={{ mb: 2 }}
                      />
                    )}
                  </Box>
                )}
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<QrCodeScanner />}
                  onClick={() => {
                    // Clear previous scan state when opening scanner
                    setSelectedProduct(null);
                    setProductDetailsOpen(false);
                    setScanResult(null);
                    setScannerOpen(true);
                  }}
                  disabled={user && user.role !== 'admin' && user.remaining_requests !== undefined && user.remaining_requests <= 0}
                  sx={{ px: 4, py: 1.5 }}
                >
                  Începe Scanarea
                </Button>
              </CardContent>
            </Card>
          </Grid>

          {/* Scan History */}
          <Grid size={12}>
            <Card>
              <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                  <History sx={{ mr: 1 }} />
                  Scanări Recente
                </Typography>
                {scanHistory.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <History sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="body1" color="text.secondary">
                      Încă nu ai scanări. Începe prin a scana codul de bare al unui produs!
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {scanHistory.slice(0, 10).map((scan, index) => (
                      <ListItem
                        key={index}
                        component="div"
                        onClick={() => handleHistoryItemClick(scan.barcode)}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          mb: 1,
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          }
                        }}
                      >
                        <ListItemIcon>
                          <QrCodeScanner color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary={scan.product_name || `Cod de bare: ${scan.barcode}`}
                          secondary={`Scanat pe ${new Date(scan.scanned_at).toLocaleDateString('ro-RO')} la ${new Date(scan.scanned_at).toLocaleTimeString('ro-RO')}`}
                        />
                        <ListItemIcon>
                          <ArrowForwardIos fontSize="small" color="action" />
                        </ListItemIcon>
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Container>

      {/* Barcode Scanner Dialog */}
      <BarcodeScanner
        open={scannerOpen}
        onClose={handleScannerClose}
        onScan={handleScanResult}
      />

      {/* Product Photo Capture Dialog */}
      <ProductPhotoCapture
        open={photoCaptureOpen}
        onClose={() => setPhotoCaptureOpen(false)}
        onComplete={handlePhotoCaptureComplete}
        barcode={currentBarcode}
      />

      {/* Product Details Dialog */}
      <ProductDetails
        open={productDetailsOpen}
        onClose={() => setProductDetailsOpen(false)}
        product={selectedProduct}
      />

      {/* Scan Result Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={scanResult?.found ? "success" : "info"} 
          sx={{ width: '100%' }}
        >
          {scanResult?.message || 'Barcode scanned'}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Dashboard;